import { easeOutQuad } from '@lemonade-hq/cdk';
import { clampedLerp } from '@lemonade-hq/ts-helpers';
import { useEffect, useRef, useState } from 'react';

export default function useOptionalTween(t: number, active: boolean, durationMultiplier: number): number {
  const prevT = useRef<number | null>(null);
  const [currentT, setCurrentT] = useState<number | null>(null);
  const startTime = useRef<number | null>(null);
  const raf = useRef<number | null>(null);

  const endAnimation = (): void => {
    if (raf.current != null) window.cancelAnimationFrame(raf.current);
    prevT.current = t;
    raf.current = null;
  };

  const animationFrame = (): void => {
    if (startTime.current == null || prevT.current == null) return;

    const currentTime = new Date().getTime();
    const dt = currentTime - startTime.current;
    const duration = Math.abs(t - prevT.current) * durationMultiplier;
    if (duration === 0) return;

    const rel = easeOutQuad(Math.min(1, dt / duration));
    const relT = clampedLerp(prevT.current, t, rel);
    setCurrentT(relT);
    if (rel < 1) {
      raf.current = window.requestAnimationFrame(animationFrame);
    } else {
      endAnimation();
    }
  };

  useEffect(() => {
    if (active && raf.current == null) {
      startTime.current = new Date().getTime();
      raf.current = window.requestAnimationFrame(animationFrame);
    } else {
      endAnimation();
    }

    return endAnimation;
  }, [active, endAnimation, animationFrame]);

  if (!active) {
    prevT.current = t;
  }

  return active && currentT != null ? currentT : t;
}
